import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Mail, Calendar, Users, FileText,
  Building2, AlertCircle, Loader2, Save, Plus, ChevronDown, X, Search, Check
} from "lucide-react";
import {
  createCampaignsRes,
  fetchUserTemplates,
  getCustomerGroupsRes,
  identityManagementRes,
  updateCampaigns,
  getCampaignsByIdRes,
} from "core";
import { toast } from "components/ui/toast";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "components/ui/select";
import { Page } from "components/ui/page";
import PageHeader from "components/ui/PageHeader";

// ==== Helpers ====
function getTodayDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
}

// ==== Shared field primitives ====
const inputCls = (hasError?: boolean) =>
  `!h-11 !min-h-11 w-full px-3.5 !py-0 text-[13px] leading-[1.25] font-medium rounded-xl border transition-all duration-150 outline-none
  bg-surface dark:bg-dark-700 text-content dark:text-white
  placeholder:text-content-subtle dark:placeholder:text-content-muted
  ${hasError
    ? "border-red-400 dark:border-red-500/60 focus:ring-2 focus:ring-red-500/20"
    : "border-line dark:border-dark-600 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
  }`;

const selectTriggerCls = (hasError?: boolean) =>
  `!h-11 !min-h-11 w-full rounded-xl px-3 !py-0 text-[13px] leading-[1.25] font-medium
  bg-surface dark:bg-dark-700 text-content dark:text-white
  ${hasError ? "border-red-400 dark:border-red-500/60" : "border-line dark:border-dark-600"}`;

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  icon?: React.ElementType;
  children: React.ReactNode;
}
const Field = ({ label, required, error, icon: Icon, children }: FieldProps) => (
  <div className="space-y-1.5">
    <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-content-muted dark:text-gray-400">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
      {required && <span className="text-blue-500 dark:text-blue-400">*</span>}
    </label>
    {children}
    {error && (
      <p className="flex items-center gap-1 text-[11px] font-semibold text-red-600 dark:text-red-400">
        <AlertCircle className="w-3 h-3 flex-shrink-0" />
        {error}
      </p>
    )}
  </div>
);

// ==== Section header ====
const SectionHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="flex-1 h-px bg-gray-100 dark:bg-dark-700" />
    <div className="text-center">
      <p className="text-[11px] font-bold uppercase tracking-widest text-content-subtle dark:text-gray-500">{title}</p>
      {subtitle && <p className="text-[10px] font-medium text-content-subtle dark:text-gray-600 mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex-1 h-px bg-gray-100 dark:bg-dark-700" />
  </div>
);

interface GroupOption {
  value: string;
  text: string;
  leadsCount?: number;
}

interface AudienceMultiSelectProps {
  options: GroupOption[];
  value: string[];
  onChange: (value: string[]) => void;
  hasError?: boolean;
  placeholder?: string;
}

const AudienceMultiSelect = ({
  options,
  value,
  onChange,
  hasError,
  placeholder = "Select audience groups...",
}: AudienceMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 640
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [dropdownRect, setDropdownRect] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    width: 0,
    maxH: 220,
    openAbove: false,
  });

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    setIsMobile(mq.matches);
    const handleChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  const calcPos = useCallback(() => {
    if (!buttonRef.current || isMobile) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openAbove = spaceBelow < 240 && spaceAbove > spaceBelow;
    setDropdownRect({
      top: rect.bottom + 6,
      bottom: window.innerHeight - rect.top + 6,
      left: rect.left,
      width: rect.width,
      maxH: Math.max((openAbove ? spaceAbove : spaceBelow) - 16, 120),
      openAbove,
    });
  }, [isMobile]);

  useEffect(() => {
    if (!open || isMobile) return;
    calcPos();
    window.addEventListener("scroll", calcPos, true);
    window.addEventListener("resize", calcPos);
    return () => {
      window.removeEventListener("scroll", calcPos, true);
      window.removeEventListener("resize", calcPos);
    };
  }, [open, isMobile, calcPos]);

  useEffect(() => {
    if (!open || isMobile) return;
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, isMobile]);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), isMobile ? 200 : 50);
    else setSearch("");
  }, [open, isMobile]);

  useEffect(() => {
    if (!open || !isMobile) return;
    document.body.classList.add("sheet-open");
    return () => document.body.classList.remove("sheet-open");
  }, [open, isMobile]);

  const toggle = (nextValue: string) => {
    onChange(value.includes(nextValue) ? value.filter((item) => item !== nextValue) : [...value, nextValue]);
  };

  const removeChip = (nextValue: string, event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(value.filter((item) => item !== nextValue));
  };

  const filtered = options.filter((option) =>
    option.text.toLowerCase().includes(search.toLowerCase())
  );
  const selectedOptions = options.filter((option) => value.includes(option.value));
  const totalContacts = selectedOptions.reduce((sum, option) => sum + (option.leadsCount ?? 0), 0);

  const listContent = (listMaxH?: number) => (
    <>
      {filtered.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-line-subtle dark:border-dark-700 bg-gray-50/50 dark:bg-dark-700/30 flex-shrink-0">
          <span className="text-[11px] font-semibold text-content-subtle uppercase tracking-wide">
            {filtered.length} group{filtered.length !== 1 ? "s" : ""}
          </span>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onChange(filtered.map((option) => option.value))}
              className="text-[12px] font-semibold text-violet-600 dark:text-violet-400 hover:underline"
            >
              Select all
            </button>
            {value.length > 0 && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-[12px] font-semibold text-content-subtle hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                  Clear
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div
        data-sheet-scroll="true"
        className="overflow-y-auto py-1 flex-1 min-h-0"
        style={{
          WebkitOverflowScrolling: "touch" as any,
          overflowY: "auto",
          touchAction: "pan-y",
          ...(listMaxH ? { maxHeight: listMaxH } : {}),
        }}
      >
        {filtered.length === 0 ? (
          <p className="text-center text-[13px] text-content-subtle dark:text-gray-500 py-8">No groups found</p>
        ) : (
          filtered.map((option) => {
            const isChecked = value.includes(option.value);
            return (
              <button
                type="button"
                key={option.value}
                onClick={() => toggle(option.value)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-100
                  ${isChecked ? "bg-violet-50 dark:bg-violet-500/10" : "hover:bg-gray-50 dark:hover:bg-dark-700/50"}`}
              >
                <div
                  className={`rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all
                    ${isChecked ? "bg-violet-600 border-violet-600" : "border-line-strong dark:border-dark-500 bg-surface dark:bg-dark-800"}`}
                  style={{ width: 18, height: 18 }}
                >
                  {isChecked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-ui font-semibold truncate
                    ${isChecked ? "text-violet-700 dark:text-violet-300" : "text-gray-800 dark:text-gray-200"}`}>
                    {option.text}
                  </p>
                </div>
                {option.leadsCount != null && (
                  <span className={`flex-shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full tabular-nums
                    ${isChecked
                      ? "bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-500/30"
                      : "bg-gray-100 dark:bg-dark-700 text-content-muted dark:text-gray-400 border border-line dark:border-dark-600"
                    }`}
                  >
                    {option.leadsCount.toLocaleString()} contacts
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>

      {value.length > 0 && (
        <div className="border-t border-line-subtle dark:border-dark-700 px-4 py-2.5 flex-shrink-0 bg-violet-50/60 dark:bg-violet-500/[0.08] flex items-center justify-between">
          <span className="text-[12px] font-semibold text-violet-700 dark:text-violet-400">
            {value.length} group{value.length !== 1 ? "s" : ""} selected
          </span>
          <span className="text-[12px] font-bold text-violet-700 dark:text-violet-300">
            {totalContacts.toLocaleString()} contacts
          </span>
        </div>
      )}
    </>
  );

  const triggerChips = value.length === 0 ? (
    <span className="self-center truncate text-ui leading-[1.25] font-medium text-content-subtle dark:text-gray-500">{placeholder}</span>
  ) : (
    value.map((selectedValue) => {
      const option = options.find((item) => item.value === selectedValue);
      if (!option) return null;
      return (
        <span
          key={selectedValue}
          className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-lg text-[12px] font-semibold bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-500/30 max-w-[180px]"
        >
          <span className="truncate">{option.text}</span>
          {option.leadsCount != null && (
            <span className="text-[10px] opacity-70 ml-0.5 tabular-nums">{option.leadsCount.toLocaleString()}</span>
          )}
          <button
            type="button"
            onClick={(event) => removeChip(selectedValue, event)}
            className="ml-0.5 rounded hover:bg-violet-200 dark:hover:bg-violet-500/30 p-0.5 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      );
    })
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`${value.length > 0 ? "!min-h-11" : "!h-11 !min-h-11"} w-full rounded-xl text-ui leading-[1.25] font-medium text-left
          bg-surface dark:bg-dark-700
          border ${hasError ? "border-red-400 dark:border-red-500" : open ? "border-indigo-400 ring-2 ring-indigo-400/15" : "border-line dark:border-dark-600"}
          transition-all duration-200 px-3 !py-0 flex items-center gap-2`}
      >
        <div className={`${value.length > 0 ? "min-h-[28px] flex-wrap" : "h-full min-w-0"} flex-1 flex items-center gap-1.5`}>
          {triggerChips}
        </div>
        <div className="flex items-center gap-1.5 self-center flex-shrink-0 ml-1">
          {value.length > 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-600 dark:text-violet-400 border border-violet-500/20">
              {value.length} selected
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-content-subtle transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {isMobile && createPortal(
        open ? (
          <>
            <div
              className="fixed inset-0 bg-black/55 z-[9998]"
              style={{ animation: "fadeIn 0.18s ease both" }}
              onClick={() => setOpen(false)}
            />
            <div
              className="fixed bottom-0 left-0 right-0 z-[9999] bg-surface dark:bg-dark-800 rounded-t-2xl flex flex-col safe-padding-bottom"
              style={{ maxHeight: "85dvh", animation: "sheetSlideUp 0.26s cubic-bezier(0.32,0.72,0,1) both" }}
            >
              <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
                <div className="w-8 h-[3px] rounded-full bg-gray-300 dark:bg-dark-500" />
              </div>
              <div className="flex items-center justify-between px-4 pt-1.5 pb-2 flex-shrink-0">
                <div className="flex-1 min-w-0">
                  <h3 className="text-title-sm font-bold text-content dark:text-white leading-snug">Audience Groups</h3>
                  <p className={`text-[11px] mt-0.5 ${value.length > 0 ? "text-violet-600 dark:text-violet-400 font-semibold" : "text-content-subtle dark:text-gray-500"}`}>
                    {value.length > 0
                      ? `${value.length} group${value.length !== 1 ? "s" : ""} · ${totalContacts.toLocaleString()} contacts`
                      : "Select one or more groups to target"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="ml-3 flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 dark:bg-dark-600 text-content-muted dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-500 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="px-4 pb-2 flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle" />
                  <input
                    ref={searchRef}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search groups..."
                    className="w-full pl-8 pr-3 py-2 text-[13px] rounded-xl bg-gray-100 dark:bg-dark-700 border border-transparent focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/15 text-content dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none transition-all"
                  />
                </div>
              </div>
              <div className="flex-1 flex flex-col overflow-hidden min-h-0 border-t border-line-subtle dark:border-dark-700">
                {listContent(undefined)}
              </div>
              <div className="px-4 pt-2.5 pb-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="w-full py-3.5 rounded-xl text-[14px] font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600 active:scale-[0.98] shadow-md shadow-indigo-500/20 transition-all duration-150"
                >
                  {value.length > 0 ? `Confirm · ${value.length} group${value.length !== 1 ? "s" : ""} selected` : "Done"}
                </button>
              </div>
            </div>
          </>
        ) : null,
        document.body
      )}

      {open && !isMobile && (
        <div
          className="bg-surface dark:bg-dark-800 border border-line dark:border-dark-600 rounded-xl shadow-xl shadow-black/10 dark:shadow-black/40 overflow-hidden flex flex-col"
          style={{
            position: "fixed",
            ...(dropdownRect.openAbove ? { bottom: dropdownRect.bottom } : { top: dropdownRect.top }),
            left: dropdownRect.left,
            width: dropdownRect.width,
            zIndex: 9999,
          }}
        >
          <div className="p-2 border-b border-line-subtle dark:border-dark-700 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle" />
              <input
                ref={searchRef}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search groups..."
                className="w-full pl-8 pr-3 py-2 text-[13px] rounded-lg bg-gray-50 dark:bg-dark-700 border border-line dark:border-dark-600 text-content dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400/20 transition-all"
              />
            </div>
          </div>
          {listContent(Math.max(dropdownRect.maxH - 90, 100))}
        </div>
      )}
    </div>
  );
};

// ==== Main component ====
export default function CampaignForm() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const [campaignsId, setCampaignsId] = useState<string | null>(null);
  const [campaignsName, setCampaignsName] = useState("");
  const [campaignSubject, setCampaignSubject] = useState("");
  const [templateType, setTemplateType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [leadsGroups, setLeadsGroups] = useState<string[]>([]);
  const [mailToUse, setMailToUse] = useState("");
  const [friendlyName, setFriendlyName] = useState("");
  const [actionType, setActionType] = useState<"Create" | "Update">("Create");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [options, setOptions] = useState<{ value: string; text: string }[]>([]);
  const [groupList, setGroupList] = useState<GroupOption[]>([]);
  const [identities, setIdentities] = useState<{ value: string; text: string }[]>([]);
  const [apiLoading, setApiLoading] = useState(false);
  const today = getTodayDate();

  useEffect(() => {
    if (params?.id && params.id !== "new") {
      setActionType("Update");
      setCampaignsId(params.id);
      getCampaignsByIdRes(params.id).then((res: any) => {
        const data = res.data;
        setCampaignsName(data.name);
        setCampaignSubject(data.subject);
        setTemplateType(data.template_id);
        setStartDate(data.start_date.substring(0, 10));
        setEndDate(data.end_date.substring(0, 10));
        const customerGroupId = data.Customer_group_id;
        setLeadsGroups(
          Array.isArray(customerGroupId)
            ? customerGroupId.map((id: any) => String(id))
            : customerGroupId
              ? [String(customerGroupId)]
              : []
        );
        setMailToUse(data.emailUsed);
        setFriendlyName(data.friendlyName || "");
      });
    }

    /* No fixture fallback. This used to fall back to
       `[{ value: "123", text: "Template A" }]` — a template that does not
       exist, selectable, and written to the database as `tid: "123"` on a
       campaign that could then never render. An account with no templates
       yet gets an empty list and the field's own empty state, which is the
       true answer. */
    fetchUserTemplates()
      .then((res: any) => {
        const items = res?.items ?? [];
        setOptions(
          items.map((tpl: any) => ({
            value: String(tpl.id),
            text: tpl.name || `Template #${tpl.id}`,
          })),
        );
      })
      .catch(() => setOptions([]));

    getCustomerGroupsRes().then((res: any) => {
      const leadGroup = (res || []).filter((item: any) => item.leadsCount > 0 && item.status === "ACTIVE");
      setGroupList(leadGroup.map((item: any) => ({ value: String(item.id), text: item.name, leadsCount: item.leadsCount })));
    });

    identityManagementRes().then((res: any) => {
      const data = res?.data || {};
      const identityWithIndex = Object.keys(data).map((key) => ({ id: key, ...data[key] }));
      const successIdentities = identityWithIndex.filter((item: any) => item.status === "Success");
      setIdentities(successIdentities.map((item: any) => ({ value: String(item.email), text: item.email })));
    });
  }, [params?.id]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!campaignsName) newErrors.campaignsName = "Campaign name is required";
    if (!campaignSubject) newErrors.campaignSubject = "Subject is required";
    if (!templateType) newErrors.templateType = "Template is required";
    if (!startDate) newErrors.startDate = "Start Date is required";
    if (!endDate) newErrors.endDate = "End Date is required";
    if (startDate && endDate && endDate <= startDate) newErrors.endDate = "End Date must be after Start Date";
    if (!mailToUse) newErrors.mailToUse = "Please select an email";
    if (!friendlyName) newErrors.friendlyName = "Business Name is required";
    if (leadsGroups.length === 0) newErrors.leadsGroups = "Please select at least one group";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setApiLoading(true);
    const body = {
      name: campaignsName,
      subject: campaignSubject,
      tid: templateType,
      sdate: startDate ? new Date(startDate).toISOString() : null,
      edate: endDate ? new Date(endDate).toISOString() : null,
      cgrp: leadsGroups,
      mailToUse,
      friendlyName,
      cid: actionType === "Update" ? campaignsId : undefined,
    };
    try {
      if (actionType === "Create") {
        await createCampaignsRes(body);
      } else {
        await updateCampaigns(body);
      }
      toast.success(`Campaign ${actionType === "Create" ? "created" : "updated"} successfully!`);
      navigate("/campaigns?page=" + (parseInt(searchParams.get("page") || "1")));
    } catch {
      toast.error("There was an error processing your request.");
    } finally {
      setApiLoading(false);
    }
  };

  const isUpdate = actionType === "Update";

  /** Clear a single field's error as soon as the user corrects it */
  const clearError = (field: string) =>
    setErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });

  return (
    /* Was a hand-built header: its own back button, its own gradient icon tile,
       an 18px heading and a 12px subtitle — none of them on the type scale, and
       none matching the header on any other page. Now the shared anatomy, with
       the back control in the `leading` slot and the edit state as a badge. */
    <Page>
        <PageHeader
          icon={isUpdate ? <FileText className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          tone="brand"
          title={isUpdate ? "Edit campaign" : "Create campaign"}
          subtitle={
            isUpdate
              ? "Update the details of your existing campaign"
              : "Set up a new email marketing campaign"
          }
          badge={isUpdate ? "Editing" : null}
          leading={
            <button
              type="button"
              onClick={() => navigate("/campaigns")}
              aria-label="Back to campaigns"
              className="mt-0.5 inline-flex items-center justify-center w-9 h-9 flex-shrink-0 rounded-xl bg-surface border border-line text-content-muted hover:text-content hover:border-line-strong transition-colors shadow-xs focus-ring"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          }
        />

        {/* ── Form card ── */}
        <div className="relative overflow-hidden bg-surface dark:bg-dark-800 rounded-2xl border border-line dark:border-dark-700 shadow-sm">
          {/* Top accent */}
          <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />

          <form onSubmit={handleSubmit} className="p-6 md:p-8">

            {/* ── Section 1: Campaign Identity ── */}
            <SectionHeader title="Campaign Identity" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
              <Field label="Campaign Name" required icon={FileText} error={errors.campaignsName}>
                <input
                  type="text"
                  value={campaignsName}
                  onChange={(e) => { setCampaignsName(e.target.value); clearError("campaignsName"); }}
                  placeholder="e.g. Summer Promo 2026"
                  className={inputCls(!!errors.campaignsName)}
                />
              </Field>
              <Field label="Email Subject" required icon={Mail} error={errors.campaignSubject}>
                <input
                  type="text"
                  value={campaignSubject}
                  onChange={(e) => { setCampaignSubject(e.target.value); clearError("campaignSubject"); }}
                  placeholder="e.g. Don't miss our summer deals!"
                  className={inputCls(!!errors.campaignSubject)}
                />
              </Field>
              <Field label="Business Name" required icon={Building2} error={errors.friendlyName}>
                <input
                  type="text"
                  value={friendlyName}
                  onChange={(e) => { setFriendlyName(e.target.value); clearError("friendlyName"); }}
                  placeholder="Your business name"
                  className={inputCls(!!errors.friendlyName)}
                />
              </Field>
              <Field label="Sender Email" required icon={Mail} error={errors.mailToUse}>
                <Select value={mailToUse} onValueChange={(v) => { setMailToUse(v); clearError("mailToUse"); }}>
                  <SelectTrigger className={selectTriggerCls(!!errors.mailToUse)}>
                    <SelectValue className="text-ui leading-[1.25] font-medium text-content dark:text-white" placeholder="Select verified email" />
                  </SelectTrigger>
                  <SelectContent>
                    {identities.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.text}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {/* ── Section 2: Template & Audience ── */}
            <SectionHeader title="Template & Audience" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
              <Field label="Email Template" required icon={FileText} error={errors.templateType}>
                <Select value={templateType} onValueChange={(v) => { setTemplateType(v); clearError("templateType"); }}>
                  <SelectTrigger className={selectTriggerCls(!!errors.templateType)}>
                    <SelectValue className="text-ui leading-[1.25] font-medium text-content dark:text-white" placeholder="Choose a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.text}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Audience Groups" required icon={Users} error={errors.leadsGroups}>
                <AudienceMultiSelect
                  options={groupList}
                  value={leadsGroups}
                  onChange={(nextValue) => {
                    setLeadsGroups(nextValue);
                    if (nextValue.length > 0) clearError("leadsGroups");
                  }}
                  hasError={!!errors.leadsGroups}
                />
              </Field>
            </div>

            {/* ── Section 3: Schedule ── */}
            <SectionHeader title="Schedule" subtitle="Set when this campaign runs" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
              <Field label="Start Date" required icon={Calendar} error={errors.startDate}>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); clearError("startDate"); }}
                  min={today}
                  className={inputCls(!!errors.startDate)}
                />
              </Field>
              <Field label="End Date" required icon={Calendar} error={errors.endDate}>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); clearError("endDate"); }}
                  min={startDate || today}
                  className={inputCls(!!errors.endDate)}
                />
              </Field>
            </div>

            {/* ── Footer actions ── */}
            <div className="flex items-center justify-between pt-5 border-t border-line dark:border-dark-700">
              <button
                type="button"
                onClick={() => navigate("/campaigns?page=" + (searchParams.get("page") || "1"))}
                className="h-9 px-4 rounded-xl text-[13px] font-semibold text-content-secondary dark:text-gray-300 bg-surface dark:bg-dark-700 border border-line dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-600 hover:border-line-strong dark:hover:border-dark-500 transition-all shadow-sm"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={apiLoading}
                className="h-9 px-6 rounded-xl text-[13px] font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {apiLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : isUpdate ? (
                  <Save className="w-3.5 h-3.5" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
                {apiLoading ? "Processing…" : isUpdate ? "Save Changes" : "Create Campaign"}
              </button>
            </div>
          </form>
        </div>

        {/* ── Helper note ── */}
        <p className="text-center text-micro font-medium text-content-subtle">
          Fields marked with <span className="text-danger">*</span> are required
        </p>
    </Page>
  );
}
